import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Copy, FileSignature, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/i18n/LocaleContext';
import { SectionHeading } from './SectionHeading';

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (err) {
      ok = false;
    }
    document.body.removeChild(el);
    return ok;
  }
};

const AddressPlate = ({ id, icon: Icon, label, value, pending, copyLabel, onCopy, tone, rotate, delay, reduced }) => {
  const isYellow = tone === 'yellow';
  return (
    <motion.div
      initial={reduced ? {} : { opacity: 0, y: 28 }}
      whileInView={reduced ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`${id}-card`}
      className={`sticker-frame relative rounded-3xl p-7 sm:p-10 ${rotate} hover:rotate-0 transition-transform duration-300 ${
        isYellow ? 'bg-primary' : 'bg-card'
      }`}
    >
      {/* Rubber stamp */}
      <span
        className={`absolute -top-4 right-6 sm:right-10 rotate-[8deg] rounded-md border-[3px] px-3 py-1 font-display text-xs sm:text-sm font-bold uppercase tracking-widest ${
          isYellow ? 'border-accent text-accent bg-background/90' : 'border-accent text-accent bg-background'
        }`}
      >
        {pending}
      </span>

      <div className="flex items-center gap-4">
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[3px] border-ink ${
            isYellow ? 'bg-background' : 'bg-secondary'
          }`}
        >
          <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
        </span>
        <p
          className={`font-display text-lg sm:text-2xl font-bold ${
            isYellow ? 'text-primary-foreground' : 'text-foreground'
          }`}
        >
          {label}
        </p>
      </div>

      <div
        className={`mt-6 rounded-xl border-[3px] border-dashed px-5 py-5 sm:py-6 ${
          isYellow ? 'border-ink/70 bg-background' : 'border-border bg-secondary'
        }`}
      >
        <p
          data-testid={`${id}-value`}
          className="font-mono text-lg sm:text-2xl lg:text-3xl font-semibold text-primary break-all leading-snug"
        >
          {value}
        </p>
      </div>

      <div className="mt-6">
        <Button
          size="lg"
          data-testid={`${id}-copy`}
          onClick={onCopy}
          className={`h-12 px-7 text-base font-bold gap-2 border-2 border-ink shadow-[4px_4px_0_hsl(var(--ink))] hover:shadow-[2px_2px_0_hsl(var(--ink))] ${
            isYellow ? 'bg-ink text-primary hover:bg-ink/85' : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          <Copy className="h-5 w-5" aria-hidden="true" />
          {copyLabel}
        </Button>
      </div>
    </motion.div>
  );
};

export const ContractSection = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();
  const c = t.contract;

  const handleCopy = async (value) => {
    const ok = await copyText(value);
    if (ok) toast.success(c.copiedToast);
  };

  return (
    <section id="contract" data-testid="contract-section" className="relative py-20 sm:py-28 border-t-2 border-border overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/lantern-chain-pattern.svg)', backgroundSize: '420px' }}
      />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeading testId="contract-heading" label={t.nav.contract} title={c.sectionTitle} intro={c.note} />
          <img
            src="/assets/01-mascot/cutouts/peipei-skeptical-flat.png"
            alt=""
            aria-hidden="true"
            className="hidden lg:block w-36 rotate-[-4deg] drop-shadow-xl shrink-0"
            loading="lazy"
          />
        </div>

        <div className="mt-16 space-y-12">
          <AddressPlate
            id="contract-address"
            icon={FileSignature}
            label={c.label}
            value={c.value}
            pending={c.pending}
            copyLabel={c.copyLabel}
            onCopy={() => handleCopy(c.value)}
            tone="yellow"
            rotate="rotate-[-1deg]"
            delay={0}
            reduced={reduced}
          />
          <AddressPlate
            id="creator-wallet"
            icon={Wallet}
            label={c.creatorFundLabel}
            value={c.creatorFundValue}
            pending={c.pending}
            copyLabel={c.copyLabel}
            onCopy={() => handleCopy(c.creatorFundValue)}
            tone="dark"
            rotate="rotate-[1deg]"
            delay={0.08}
            reduced={reduced}
          />
        </div>
      </div>
    </section>
  );
};
